import PropTypes from "prop-types";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { buttonVariants } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { BaseForm, FormInput } from "components/BaseForm";
import { REQUIRED_MARK } from "components/BaseForm/fields/fieldClasses";
import { useVoicemailMutations } from "hooks/useVoicemailTable";
import AttachmentService from "services/Attachment";
import VoicemailService from "services/Voicemails";
import useAuthStore from "stores/useAuthStore";

const makeSchema = ({ isEdit }) =>
  z
    .object({
      name: z.string().min(1, "Name is required"),
      is_shared: z.boolean(),
      file: z.any().nullable(),
    })
    .superRefine((data, ctx) => {
      if (!isEdit && !data.file) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "File is required",
          path: ["file"],
        });
      }
    });

const VoicemailForm = ({ model, onSuccess, onCancel }) => {
  const isEdit = !!model?.id;
  const userAuth = useAuthStore(s => s.userAuth);
  const { create, update } = useVoicemailMutations();

  const { data: fetched, isLoading } = useQuery({
    queryKey: ["voicemail", model?.id],
    queryFn: () => VoicemailService.getVoicemail(model.id),
    enabled: isEdit,
  });

  const schema = useMemo(() => makeSchema({ isEdit }), [isEdit]);

  if (isEdit && (isLoading || !fetched)) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:p-6">
        <Spinner />
      </div>
    );
  }

  const source = fetched ?? {};
  const attachmentName = source.attachment?.file_name ?? "";
  const shouldDisableIsShared =
    !!source.creator_user_id && source.creator_user_id !== userAuth?.id;

  const defaults = {
    name: source.name ?? "",
    is_shared: source.is_shared ?? false,
    file: null,
  };

  const handleSubmit = async values => {
    const data = { name: values.name, is_shared: values.is_shared };
    if (values.file) {
      // Voicemail uploads are publicly available for now: the mp3 is fetched by
      // key alone (no query params), which requires the object to be public.
      const attachment = await AttachmentService.uploadFile(values.file);
      data.attachments = [attachment];
    }
    if (isEdit) {
      await update.mutateAsync({ id: source.id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  const handleSuccess = () => {
    toast.success(isEdit ? "Voicemail updated" : "Voicemail created");
    onSuccess?.();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaults}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
      submitButtonText="Save"
    >
      <FormInput name="name" label="Name" required />
      <Controller
        name="file"
        render={({
          field: { onChange, ref, onBlur, name, value },
          fieldState: { error },
        }) => (
          <Field data-invalid={error ? true : undefined}>
            <FieldLabel
              className={!isEdit ? REQUIRED_MARK : undefined}
              htmlFor="voicemail-record-input"
            >
              File
            </FieldLabel>
            <div className="tw:flex tw:items-center tw:gap-2">
              {/* The native file input's "Choose File / No file chosen" text
                  is browser shadow DOM and can't be relabeled, so the input is
                  visually hidden and this label drives it. */}
              <input
                ref={ref}
                id="voicemail-record-input"
                name={name}
                type="file"
                accept=".mp3"
                className="tw:peer tw:sr-only"
                onBlur={onBlur}
                onChange={e => onChange(e.target.files?.[0] ?? null)}
              />
              <label
                htmlFor="voicemail-record-input"
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "tw:cursor-pointer",
                  "tw:peer-focus-visible:border-ring tw:peer-focus-visible:ring-3 tw:peer-focus-visible:ring-ring/50"
                )}
              >
                Select a file
              </label>
              <span className="tw:min-w-0 tw:truncate tw:text-sm tw:text-muted-foreground">
                {value?.name ?? (isEdit ? "" : "No file selected")}
              </span>
            </div>
            {isEdit && attachmentName && (
              <FieldDescription>Current: {attachmentName}</FieldDescription>
            )}
            <FieldError>{error?.message}</FieldError>
          </Field>
        )}
      />
      <Controller
        name="is_shared"
        render={({ field }) => {
          const switchEl = (
            <Switch
              id="is_shared"
              checked={!!field.value}
              onCheckedChange={checked => field.onChange(!!checked)}
              disabled={shouldDisableIsShared}
            />
          );
          return (
            <Field>
              <div className="tw:flex tw:items-center tw:gap-2">
                {shouldDisableIsShared ? (
                  <Tooltip>
                    <TooltipTrigger
                      className="tw:cursor-help tw:border-0 tw:bg-transparent tw:p-0"
                      aria-label="Shared toggle disabled"
                    >
                      {switchEl}
                    </TooltipTrigger>
                    <TooltipContent>
                      You can only set this toggle for Voicemails that you have
                      created.
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  switchEl
                )}
                <FieldLabel htmlFor="is_shared">Shared</FieldLabel>
              </div>
            </Field>
          );
        }}
      />
    </BaseForm>
  );
};

VoicemailForm.propTypes = {
  model: PropTypes.object,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default VoicemailForm;
